// v189 reverse-dependency search.
//
// "When a CVE drops, who pulled the affected package?" — the answer
// has historically required SSH'ing to the DB. This page wraps the
// /api/admin/security/pulls endpoint with a search form + grouped
// result table so a SOC analyst can answer the question in 30
// seconds instead of an afternoon.

import { useState, type FormEvent } from 'react';
import { Search, Users, RefreshCw, AlertTriangle } from 'lucide-react';
import { SecurityShell } from '../../components/SecurityShell';
import { Card, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { usePullSearch, type ArtifactPullGroup, type PullSearchFilters } from '../../api/security';

function fmtDate(s: string | undefined): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

// Short SHA shown in the table — full digest is a tooltip on hover.
function shortSha(s: string): string {
  if (!s) return '';
  return s.length > 12 ? s.slice(0, 12) + '…' : s;
}

function principalTone(kind: string): 'accent' | 'nebula' | 'warning' {
  if (kind === 'user') return 'accent';
  if (kind === 'service_account') return 'nebula';
  return 'warning';
}

interface PullsTableBodyProps {
  isLoading: boolean;
  error: unknown;
  groups: ArtifactPullGroup[];
}

function PullsTableBody({ isLoading, error, groups }: PullsTableBodyProps) {
  if (isLoading) {
    return (
      <tr>
        <td colSpan={7} className="px-5 py-12 text-center text-fg-muted text-sm">Loading…</td>
      </tr>
    );
  }
  if (error) {
    return (
      <tr>
        <td colSpan={7} className="px-5 py-12 text-center text-danger text-sm">
          <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
          Search failed. Check your filters and try again.
        </td>
      </tr>
    );
  }
  if (groups.length === 0) {
    return (
      <tr>
        <td colSpan={7} className="px-5 py-12 text-center text-fg-muted text-sm">
          <Users className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
          No pulls match these filters. Either nobody fetched the
          artifact in the time window, or the audit table hasn't
          recorded one yet (rows are written async on every pull).
        </td>
      </tr>
    );
  }
  return (
    <>
      {groups.map((g, i) => (
        <tr
          key={`${g.principal_email}-${g.sha256}-${i}`}
          className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40"
        >
          <td className="px-5 py-3">
            <div className="flex items-center gap-2">
              <Badge tone={principalTone(g.principal_kind)}>{g.principal_kind}</Badge>
              <span className="text-xs text-fg">
                {g.principal_email || <span className="italic text-fg-subtle">(anonymous)</span>}
              </span>
            </div>
          </td>
          <td className="px-5 py-3 text-fg text-xs max-w-sm">
            <div className="font-medium">
              {g.package_name || g.artifact_path || '—'}
            </div>
            {g.project_slug && g.repo_slug && (
              <div className="text-fg-muted text-[11px] mt-0.5">
                {g.project_slug}/{g.repo_slug}
              </div>
            )}
          </td>
          <td className="px-5 py-3 font-mono text-xs text-fg-muted" title={g.sha256}>
            {shortSha(g.sha256)}
          </td>
          <td className="px-5 py-3 text-right text-fg font-medium">{g.count}</td>
          <td className="px-5 py-3 text-right text-fg-muted">{g.distinct_ips}</td>
          <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
            {fmtDate(g.first_pulled_at)}
          </td>
          <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
            {fmtDate(g.last_pulled_at)}
          </td>
        </tr>
      ))}
    </>
  );
}

export function WhoPulled() {
  // Form state lives separately from the SWR key so the user can
  // tweak inputs and only fire the query on submit. This keeps the
  // 30-day wide-window query off the page-load critical path.
  const [pkg, setPkg] = useState('');
  const [sha, setSha] = useState('');
  const [cve, setCve] = useState('');
  const [principal, setPrincipal] = useState('');
  const [since, setSince] = useState('');
  const [filters, setFilters] = useState<PullSearchFilters | null>(null);

  const { data, isLoading, error, mutate } = usePullSearch(filters);

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setFilters({
      package: pkg.trim() || undefined,
      sha256: sha.trim() || undefined,
      cve: cve.trim() || undefined,
      principal: principal.trim() || undefined,
      since: since.trim() || undefined,
    });
  };

  const onClear = () => {
    setPkg(''); setSha(''); setCve(''); setPrincipal(''); setSince('');
    setFilters(null);
  };

  const groups = data?.groups ?? [];

  return (
    <SecurityShell
      eyebrow="Audit · supply-chain"
      title="Who pulled this?"
      description={
        <>
          Reverse-dependency search across every artifact pull recorded by
          the registry. Filter by package name, sha256 digest, CVE / GHSA
          advisory, principal, or time window. Results are grouped per
          (principal, artifact) pair so one row = one identity that pulled
          one CAS row.
        </>
      }
    >
      <Card>
        <CardHeader title="Search" description="Combine filters to narrow. Empty form defaults to the last 30 days, all principals." />
        <form onSubmit={onSearch} className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-medium text-fg-muted">Package name</span>
            <input
              type="text"
              value={pkg}
              onChange={(e) => setPkg(e.target.value)}
              placeholder="log4j-core, lodash, requests"
              className="mt-1 w-full h-9 px-3 rounded-md border border-border bg-bg text-sm text-fg focus:outline-none focus:border-accent"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-fg-muted">CVE / GHSA</span>
            <input
              type="text"
              value={cve}
              onChange={(e) => setCve(e.target.value)}
              placeholder="CVE-2021-44228"
              className="mt-1 w-full h-9 px-3 rounded-md border border-border bg-bg text-sm text-fg focus:outline-none focus:border-accent"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-fg-muted">SHA-256 digest</span>
            <input
              type="text"
              value={sha}
              onChange={(e) => setSha(e.target.value)}
              placeholder="hex (with or without sha256: prefix)"
              className="mt-1 w-full h-9 px-3 rounded-md border border-border bg-bg text-sm text-fg focus:outline-none focus:border-accent font-mono"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-fg-muted">Principal (email or service account)</span>
            <input
              type="text"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              placeholder="alice@example.com"
              className="mt-1 w-full h-9 px-3 rounded-md border border-border bg-bg text-sm text-fg focus:outline-none focus:border-accent"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-medium text-fg-muted">Since (RFC3339 timestamp)</span>
            <input
              type="text"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              placeholder="2026-04-01T00:00:00Z (default: last 30 days)"
              className="mt-1 w-full h-9 px-3 rounded-md border border-border bg-bg text-sm text-fg focus:outline-none focus:border-accent font-mono"
            />
          </label>
          <div className="md:col-span-2 flex items-center gap-2 pt-2">
            <Button type="submit" variant="primary">
              <Search className="h-4 w-4 mr-1" />
              Search
            </Button>
            <button
              type="button"
              onClick={onClear}
              className="h-9 px-3 rounded-md text-sm border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
            >
              Clear
            </button>
            {filters !== null && (
              <button
                type="button"
                onClick={() => mutate()}
                className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </button>
            )}
          </div>
        </form>
      </Card>

      {filters !== null && (
        <Card>
          <CardHeader
            title={`${groups.length} result${groups.length === 1 ? '' : 's'}`}
            description={
              data?.since
                ? `Pulls since ${fmtDate(data.since)}`
                : 'Searching pull history…'
            }
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-5 py-3 font-medium text-fg-muted">Principal</th>
                  <th className="px-5 py-3 font-medium text-fg-muted">Package</th>
                  <th className="px-5 py-3 font-medium text-fg-muted">SHA-256</th>
                  <th className="px-5 py-3 font-medium text-fg-muted text-right">Pulls</th>
                  <th className="px-5 py-3 font-medium text-fg-muted text-right">IPs</th>
                  <th className="px-5 py-3 font-medium text-fg-muted whitespace-nowrap">First</th>
                  <th className="px-5 py-3 font-medium text-fg-muted whitespace-nowrap">Last</th>
                </tr>
              </thead>
              <tbody>
                <PullsTableBody isLoading={isLoading} error={error} groups={groups} />
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </SecurityShell>
  );
}
